import { DebuggingService, SessionStorageKeys } from '@ic-monorepo/shared-utils';
import { Auth, signOut } from '@angular/fire/auth';
import { Router } from '@angular/router';
import {
  DataAccessPatientUserSessionsService,
  DataAccessPatientUsersService,
} from '@ic-monorepo/data-access-firestore';
import { ExtendedWindow } from '@ic-monorepo/shared-injection-tokens';
import { SessionStorageService } from '@ic-monorepo/shared-services';
import { SubmissionFlowStore } from '@ic-monorepo/shared-submissions';
import { PatientSessionWatchService } from '@ic-monorepo/shared-auth-patient';
import { PatientUserStore } from '@ic-monorepo/shared-stores';
import { Environment } from '@ic-monorepo/shared-types';
import { catchError, exhaustMap, from, map, Observable, of, switchMap, take, tap, timeout } from 'rxjs';

const debuggerContext = 'PatientSessionInit';

// Guards the blocking app initializer below: a hung or failed Firestore read must
// never leave `ApplicationInitStatus.donePromise` unresolved, or `AppComponent`
// never mounts and the app stays blank indefinitely.
const HYDRATE_PATIENT_USER_TIMEOUT_MS = 10_000;

/**
 * Hydrates the `PatientUserStore` from the current Firebase Auth user on app
 * startup. Returns an observable that completes once the store is populated
 * (or confirmed empty). Angular's `provideAppInitializer` subscribes to this
 * and blocks the bootstrap until it completes.
 *
 * Covers the page-refresh scenario: `browserSessionPersistence` (Firebase Auth)
 * survives a refresh but not a tab close, so if the user is still authed we
 * reload their patient user document into the store.
 */
export function hydratePatientUserStore(
  auth: Auth,
  dataAccessPatientUserService: DataAccessPatientUsersService,
  patientUserStore: InstanceType<typeof PatientUserStore>,
  debug: DebuggingService,
): Observable<void> {
  debug.info(debuggerContext, 'Hydrating patient user store');

  return from(auth.authStateReady()).pipe(
    switchMap(() => {
      if (!auth.currentUser) {
        debug.info(debuggerContext, 'No authenticated user, skipping hydration');
        return of(null);
      }
      debug.info(debuggerContext, 'Auth user found, loading patient user:', auth.currentUser.uid);
      return dataAccessPatientUserService.getPatientUser$(auth.currentUser.uid).pipe(
        timeout({ first: HYDRATE_PATIENT_USER_TIMEOUT_MS }),
        catchError(error => {
          debug.error(debuggerContext, 'Failed to load patient user, continuing without hydration:', error);
          return of(null);
        }),
      );
    }),
    tap(patientUser => {
      if (patientUser) {
        debug.info(debuggerContext, 'Patient user loaded, updating store:', patientUser.id);
        patientUserStore.updateStore({ patientUser });
      } else {
        debug.info(debuggerContext, 'No patient user found');
      }
    }),
    take(1),
    map(() => void 0),
  );
}

/**
 * Resumes `PatientSessionWatchService` if a session ID exists in
 * `sessionStorage` (page refresh scenario). Both `sessionStorage` and
 * `browserSessionPersistence` survive page refresh but not tab close.
 *
 * Must be called after:
 * - `subscribeToSessionExpiry` — so the expiry handler is ready to catch
 *   already-expired sessions
 * - `hydratePatientUserStore` — so the watch service has a patient user
 *   context to work with
 */
export function resumeSessionWatch(
  sessionStorageService: SessionStorageService,
  patientSessionWatchService: PatientSessionWatchService,
  debug: DebuggingService,
): void {
  const sessionId = sessionStorageService.getItem<string>(SessionStorageKeys.PATIENT_SESSION_ID);
  if (sessionId) {
    debug.info(debuggerContext, 'Resuming session watch for:', sessionId);
    patientSessionWatchService.start(sessionId);
  } else {
    debug.info(debuggerContext, 'No session ID in sessionStorage, skipping resume');
  }
}

/**
 * Subscribes to `sessionExpired$` from the watch service. This is a
 * fire-and-forget subscription that lives for the lifetime of the app.
 *
 * When a session expires (inactivity or absolute timeout):
 * 1. Stops the watch service and clears local state (store + sessionStorage)
 * 2. Clears any in-progress submission data
 * 3. Soft-deletes the Firestore session document (sets `expiredAt`)
 * 4. Signs out of Firebase Auth
 * 5. Redirects to the identity page, or to NHS login on a referral shell route
 */
export function subscribeToSessionExpiry(
  patientSessionWatchService: PatientSessionWatchService,
  dataAccessPatientUserSessionsService: DataAccessPatientUserSessionsService,
  patientUserStore: InstanceType<typeof PatientUserStore>,
  submissionFlowStore: SubmissionFlowStore,
  sessionStorageService: SessionStorageService,
  auth: Auth,
  router: Router,
  environment: Environment,
  window: ExtendedWindow,
  debug: DebuggingService,
): void {
  debug.info(debuggerContext, 'Subscribing to session expiry');

  patientSessionWatchService.sessionExpired$
    .pipe(
      exhaustMap(() => {
        const sessionId = sessionStorageService.getItem<string>(SessionStorageKeys.PATIENT_SESSION_ID);
        debug.warn(debuggerContext, 'Session expired, starting cleanup. Session ID:', sessionId);

        // Clear local state immediately so the UI reflects logged-out status
        patientSessionWatchService.stop();
        patientUserStore.clearState();
        debug.info(debuggerContext, 'Local state cleared');

        // Clean up remote state, then redirect
        return clearSubmissionData(submissionFlowStore, debug).pipe(
          switchMap(() =>
            auth.currentUser ? expireSession(dataAccessPatientUserSessionsService, sessionId, auth, debug) : of(void 0),
          ),
          switchMap(() => {
            debug.info(debuggerContext, 'Signing out of Firebase Auth');
            return from(signOut(auth)).pipe(catchError(() => of(void 0)));
          }),
          tap(() => redirectAfterExpiry(router, environment, window, debug)),
          catchError(error => {
            debug.error(debuggerContext, 'Session cleanup failed, forcing redirect:', error);
            redirectAfterExpiry(router, environment, window, debug);
            return of(void 0);
          }),
        );
      }),
    )
    .subscribe();
}

/**
 * NHS login's session management specification requires redirecting automatically
 * to the authentication journey at the end of each session, without user interaction.
 * A referral patient has no request to fall back to, so only the referral shell's
 * expiry goes back to NHS login — the existing request flow's identity-page redirect
 * is unchanged.
 */
function redirectAfterExpiry(
  router: Router,
  environment: Environment,
  window: ExtendedWindow,
  debug: DebuggingService,
): void {
  if (router.url.includes('/open-link/')) {
    debug.info(debuggerContext, 'Referral shell session expired, redirecting to NHS login');
    window.location.href = `${environment.url}/sso/nhs/patient/login`;
    return;
  }
  debug.info(debuggerContext, 'Redirecting to identity page');
  router.navigate(['/entry/new/identity']);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function clearSubmissionData(submissionFlowStore: SubmissionFlowStore, debug: DebuggingService): Observable<void> {
  return submissionFlowStore.getRequestId$.pipe(
    take(1),
    tap(requestId => {
      if (requestId) {
        debug.info(debuggerContext, 'Clearing submission data for request:', requestId);
        submissionFlowStore.clearSubmissionData(requestId);
      }
    }),
    map(() => void 0),
  );
}

function expireSession(
  dataAccessPatientUserSessionsService: DataAccessPatientUserSessionsService,
  sessionId: string | null,
  auth: Auth,
  debug: DebuggingService,
): Observable<void> {
  if (sessionId) {
    debug.info(debuggerContext, 'Expiring Firestore session:', sessionId);
    return dataAccessPatientUserSessionsService.expirePatientUserSession$(sessionId).pipe(
      catchError(error => {
        const authState = {
          hasAuth: !!auth.currentUser,
          uid: auth.currentUser?.uid ?? null,
          sessionId,
        };
        debug.error(debuggerContext, 'Failed to expire session - auth state:', authState, error);
        return of(void 0);
      }),
    );
  }
  return of(void 0);
}
